import React, { createContext, useState, useEffect } from 'react';
import useLocalStorage from 'use-local-storage';

import { useSubscription } from 'hooks/SubscriptionHook';
import { usePrediction, OAUTH_SERVERS } from 'hooks/PredictionHook';

import { createHook } from 'utils/utils';
import { chooseServer } from 'api/fetch-tokens';
import {
  getActualCcWsServerUrl,
  getActualOauthServerUrl,
  getRegistrationToken,
} from 'api/oauth-api';

export enum AutoSelectServerStatus {
  DISCONNECTED = 'DISCONNECTED',
  PROBING = 'PROBING',
  SELECTED = 'SELECTED',
}

const AUTO_SELECT_LOCAL_STORAGE_KEY = 'AutoSelectServer:isAutoSelectEnabled';

interface AutoSelectServerContextInterface {
  autoSelectServerStatus: AutoSelectServerStatus;
  isAutoSelected: boolean;
  setIsAutoSelected: (isAutoSelected: boolean) => void;
  getCcWsServerUrl: (oauthServerUrl: string) => Promise<string>;
}

type Props = {
  children: React.ReactNode;
};

// Statistics on a frame sent to a server and then received back
type FrameStat = {
  frameIndex: number;
  // Time when the frame was sent
  start: number;
  // Time when the frame was received back
  end: number;
};

type FrameStatArray = FrameStat[];

/**
 * Calculate the fastest server based on the frame statistics
 * @param stats Array of frame statistics. Each element corresponds to a server in SERVERS
 * @returns Index of the fastest server in SERVERS array
 */
export const findBestServerIndex = (stats: FrameStatArray[]) => {
  const frameCounts = stats.map((stat) => stat.length);
  const maxFrameCount = Math.max(...frameCounts);
  const avgDurations = stats.map((stat) => {
    if (stat.length !== maxFrameCount) {
      return Number.MAX_SAFE_INTEGER;
    }
    const durations = stat.map((frameStat) => Math.abs(frameStat.end - frameStat.start));
    const avgDuration = durations.reduce((a, b) => a + b, 0) / durations.length;
    return avgDuration;
  });
  const minAvgDuration = Math.min(...avgDurations);
  const bestServerIndex = avgDurations.indexOf(minAvgDuration);
  return bestServerIndex;
};

const AutoSelectServerContext = createContext<AutoSelectServerContextInterface | null>(null);

export const useAutoSelectServer = () => createHook('useAutoSelectServer', AutoSelectServerContext);

export const AutoSelectServerContextProvider = (props: Props) => {
  const [isAutoSelected, setIsAutoSelected] = useLocalStorage<boolean>(
    AUTO_SELECT_LOCAL_STORAGE_KEY,
    true,
  );
  const [status, setStatus] = useState<AutoSelectServerStatus>(AutoSelectServerStatus.DISCONNECTED);

  const { tokenObj, accessToken } = useSubscription();
  const { token } = tokenObj || {};

  const { setServerUrl } = usePrediction();

  const getCcWsServerUrl = async (oauthServerUrl: string): Promise<string> => {
    const [registrationToken, webSocketServerUrl] = await getRegistrationToken(oauthServerUrl);
    return webSocketServerUrl;
  };

  useEffect(() => {
    const startTestingServers = async (): Promise<void> => {
      const actualCcWsServerUrl = getActualCcWsServerUrl();
      const actualOauthServerUrl = getActualOauthServerUrl();
      if (actualCcWsServerUrl && actualOauthServerUrl) {
        return;
      }

      const serverList = OAUTH_SERVERS.map((server) => server.value);
      const [server] = await chooseServer(serverList);
      const webSocketServerUrl = await getCcWsServerUrl(server);

      setServerUrl(webSocketServerUrl);
      setStatus(AutoSelectServerStatus.SELECTED);
    };

    if (!isAutoSelected) {
      // Not autoselecting, do nothing
      setStatus(AutoSelectServerStatus.DISCONNECTED);
      return;
    } else if (status !== AutoSelectServerStatus.DISCONNECTED) {
      // We can start probing only in disconnected (initial) state,
      return;
    } else if (!token) {
      // Wait until token is available
      return;
    }
    setStatus(AutoSelectServerStatus.PROBING);
    startTestingServers();
  }, [isAutoSelected, setServerUrl, status, token, accessToken]);

  return (
    <AutoSelectServerContext.Provider
      value={{
        autoSelectServerStatus: status,
        isAutoSelected,
        setIsAutoSelected,
        getCcWsServerUrl,
      }}
    >
      {props.children}
    </AutoSelectServerContext.Provider>
  );
};
